import { useState } from "react";
import { Bell, BellOff } from "lucide-react";
import api from "@/lib/api";
import { subscribeToPush, pushSupported } from "@/lib/push";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Première connexion : demande d'activation des notifications (après le tutoriel).
// « Non » ou fermeture = jamais redemandé (flag notif_setup_seen), réglable dans Paramètres.
const PREF_ROWS = [
  ["pieces", "param.notif_pieces"],
  ["documents", "param.notif_documents"],
  ["events_created", "param.notif_events_created"],
  ["events_updated", "param.notif_events_updated"],
];

export default function NotifSetupDialog({ onClose }) {
  const { t } = useAuth();
  const [step, setStep] = useState("ask"); // ask | types
  const [prefs, setPrefs] = useState({ pieces: true, documents: true, events_created: true, events_updated: true });
  const [pushState, setPushState] = useState(null);
  const [busy, setBusy] = useState(false);

  const answerNo = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.put("/notifications/prefs", { pieces: false, documents: false, events_created: false, events_updated: false });
    } catch (e) { /* silencieux : réglable dans Paramètres */ }
    onClose();
  };

  const answerYes = async () => {
    if (busy) return;
    setBusy(true);
    if (pushSupported()) {
      try {
        await subscribeToPush();
        setPushState("granted");
      } catch {
        setPushState(typeof Notification !== "undefined" && Notification.permission === "denied" ? "denied" : "failed");
      }
    } else {
      setPushState("unsupported");
    }
    setBusy(false);
    setStep("types");
  };

  const saveTypes = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.put("/notifications/prefs", prefs);
      toast.success(t("nset.saved"));
    } catch (e) { toast.error(e.friendly); }
    onClose();
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="card-bg border-app max-w-md" data-testid="notif-setup-dialog">
        {step === "ask" ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2" data-testid="notif-setup-title">
                <Bell className="h-5 w-5 accent" /> {t("nset.title")}
              </DialogTitle>
              <DialogDescription className="t-secondary">{t("nset.desc")}</DialogDescription>
            </DialogHeader>
            <p className="font-heading text-lg font-bold text-center py-2" data-testid="notif-setup-question">{t("nset.question")}</p>
            <div className="grid grid-cols-2 gap-3">
              <Button className="accent-bg text-slate-900 font-bold h-11" onClick={answerYes} disabled={busy} data-testid="notif-setup-yes">
                <Bell className="h-4 w-4 mr-1.5" /> {t("nset.yes")}
              </Button>
              <Button variant="outline" className="border-app h-11" onClick={answerNo} disabled={busy} data-testid="notif-setup-no">
                <BellOff className="h-4 w-4 mr-1.5" /> {t("nset.no")}
              </Button>
            </div>
            <p className="text-xs t-secondary text-center">{t("nset.later_hint")}</p>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading" data-testid="notif-setup-types-title">{t("nset.types_title")}</DialogTitle>
              <DialogDescription className="t-secondary">{t("nset.types_desc")}</DialogDescription>
            </DialogHeader>
            {pushState && (
              <p className={`text-xs ${pushState === "granted" ? "text-emerald-400" : "t-secondary"}`} data-testid="notif-setup-push-state">
                {pushState === "granted" ? t("param.push_on") : pushState === "denied" ? t("param.push_denied") : pushState === "unsupported" ? t("param.push_unsupported") : t("param.push_failed")}
              </p>
            )}
            <div className="space-y-3">
              {PREF_ROWS.map(([k, label]) => (
                <label key={k} className="flex items-center justify-between text-sm cursor-pointer" data-testid={`notif-setup-row-${k}`}>
                  {t(label)}
                  <Switch checked={prefs[k]} onCheckedChange={(v) => setPrefs({ ...prefs, [k]: v })} data-testid={`notif-setup-pref-${k}`} />
                </label>
              ))}
            </div>
            <Button className="w-full accent-bg text-slate-900 font-bold h-11" onClick={saveTypes} disabled={busy} data-testid="notif-setup-save">
              {t("common.save")}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
